import { LocaleEnum, TransformationVariant } from '@amzn/fire-tv-campaign-automation-service';
import { TEXT_BASED_TITLE_DESCRIPTION_LENGTH } from 'src/constants';
import { LogoTextState } from 'src/types';

import { getProviderLogoOptions } from '../config/providerLogoConfiguration';

import { getCtaOptions } from './ctaConfiguration';

type FieldType = 'text' | 'dropdown';

interface BaseFieldConfig {
  type: FieldType;
  label: string;
  placeholder: string;
  dataTestId: string;
  required?: boolean;
}

export interface TextFieldConfig extends BaseFieldConfig {
  type: 'text';
  maxLength?: number;
}

export interface DropdownFieldConfig extends BaseFieldConfig {
  type: 'dropdown';
  options: (locale: LocaleEnum) => Array<{ name: string; value: string }>;
}

export type FieldConfig = TextFieldConfig | DropdownFieldConfig;

/**
 * Configuration for LogoTextInput form fields (FireTV - all fields)
 */
export const LOGO_TEXT_INPUT_CONFIG: Record<keyof LogoTextState, FieldConfig> = {
  logoTitle: {
    type: 'text',
    label: 'Logo Title',
    placeholder: 'Enter Logo Title',
    dataTestId: 'logo-title-input',
    maxLength: TEXT_BASED_TITLE_DESCRIPTION_LENGTH.LOGO_TITLE_MAX_LENGTH_WITH_DESCRIPTION,
    required: true,
  },
  logoDescription: {
    type: 'text',
    label: 'Logo Description',
    placeholder: 'Enter Logo Description',
    dataTestId: 'logo-description-input',
    maxLength: TEXT_BASED_TITLE_DESCRIPTION_LENGTH.LOGO_DESCRIPTION_MAX_LENGTH,
    required: false,
  },
  cta: {
    type: 'dropdown',
    label: 'Call To Action',
    placeholder: 'Select CTA',
    dataTestId: 'cta-selector',
    options: (locale: LocaleEnum) =>
      getCtaOptions(locale).map((cta) => ({
        name: cta,
        value: cta,
      })),
    required: false,
  },
  providerLogo: {
    type: 'dropdown',
    label: 'Provider Logo',
    placeholder: 'Select Provider Logo',
    dataTestId: 'provider-logo-selector',
    options: () => getProviderLogoOptions(),
    required: false,
  },
};

/**
 * Configuration for LogoTextInput form fields (FireTablets - Title + ProviderLogo only)
 */
export const LOGO_TEXT_TITLE_ONLY_CONFIG: Pick<
  Record<keyof LogoTextState, FieldConfig>,
  'logoTitle' | 'providerLogo'
> = {
  logoTitle: {
    ...(LOGO_TEXT_INPUT_CONFIG.logoTitle as TextFieldConfig),
    maxLength: TEXT_BASED_TITLE_DESCRIPTION_LENGTH.LOGO_TITLE_MAX_LENGTH_WITHOUT_DESCRIPTION,
  },
  providerLogo: { ...LOGO_TEXT_INPUT_CONFIG.providerLogo, required: true },
};

/**
 * Configuration mapping transformation variants to their required fields.
 * Only these fields must be present to initiate a transformation.
 * The useEffect in useAssetInput watches all logo text fields for changes
 * and re-triggers transformation when any of them change after Save.
 */
export const LOGO_TEXT_VARIANT_CONFIG: Partial<
  Record<TransformationVariant, { requiredForTransformation: Array<keyof LogoTextState> }>
> = {
  [TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS]: {
    requiredForTransformation: ['logoTitle', 'providerLogo'],
  },
  [TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION]: {
    requiredForTransformation: ['logoTitle'],
  },
  [TransformationVariant.NO_TRANSFORMATION]: {
    requiredForTransformation: [],
  },
};

/**
 * Returns fields required to initiate a transformation
 */
export function getRequiredForTransformation(
  variant: TransformationVariant,
): Array<keyof LogoTextState> {
  return LOGO_TEXT_VARIANT_CONFIG[variant]?.requiredForTransformation ?? [];
}

/**
 * Returns the appropriate logo text config based on transformation variant
 */
export function getLogoTextConfig(
  variant: TransformationVariant,
): Partial<Record<keyof LogoTextState, FieldConfig>> {
  return variant === TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS
    ? LOGO_TEXT_TITLE_ONLY_CONFIG
    : LOGO_TEXT_INPUT_CONFIG;
}
